import { useContext, useState } from 'react';
import dayjs from 'dayjs';
import time from 'utils/time';
import DescriptionTinyEditor from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import { store } from 'components/Store';
import PeopleAsyncSelect from './PeopleAsyncSelect';
import MembersSelect from './MembersSelect';

export const INTERACTION_TYPES = ['Email', 'Meeting', 'Phone call', 'Social media', 'Events', 'Other'];

const InteractionFormFields = ({ form, onChange }) => {
  const { state } = useContext(store);
  const teamName = state.team?.teamName ?? 'Organisation';

  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    name: '',
    range: { from: '', to: '' },
  });

  const openCalendar = () =>
    setCalendarState((s) => ({
      ...s,
      active: true,
      selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    }));

  const initiatorLabel =
    form.initiator === 'Organisation' ? teamName : form.initiator === 'Stakeholder' ? 'Stakeholder' : null;

  return (
    <>
      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p2 font-weight-bold'>Title</label>
          <input
            type='text'
            className='form-control border no-focus-outline'
            value={form.title}
            onChange={(e) => onChange('title', e.target.value)}
          />
        </div>
        <div className='col pl-2 pr-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p2 font-weight-bold'>Date</label>
          <button type='button' className='form-control border text-left no-focus-outline' onClick={openCalendar}>
            {form.date ? dayjs(form.date).format('D MMMM YYYY') : 'Select date'}
          </button>
          <CalendarPopup
            showPopup={calendarState.active}
            calendarState={calendarState}
            setCalendarState={setCalendarState}
            ApplyCalendarFilters={() => onChange('date', dayjs(calendarState.selectedDay).format('YYYY-MM-DD'))}
            setShowPopup={(val) => setCalendarState((s) => ({ ...s, active: val }))}
            noFutureDatesAllowed={false}
            selectedMonth={calendarState.selectedDay ? dayjs(calendarState.selectedDay).toDate() : new Date()}
          />
        </div>
      </div>

      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p2 font-weight-bold'>Type</label>
          <DropdownComponent
            mainItem={form.type || 'Select type'}
            fixedDropdown
            mainItemClass='form-control border no-focus-outline'
          >
            <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
              {INTERACTION_TYPES.map((t) => (
                <div key={t} className='radio-button px-3 py-2'>
                  <input
                    id={`interaction-type-${t}`}
                    type='radio'
                    checked={form.type === t}
                    onChange={() => onChange('type', t)}
                  />
                  <label htmlFor={`interaction-type-${t}`}>{t}</label>
                </div>
              ))}
            </div>
          </DropdownComponent>
        </div>
        <div className='col pl-2 pr-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p2 font-weight-bold'>Initiator</label>
          <DropdownComponent
            mainItem={initiatorLabel || 'Select initiator'}
            fixedDropdown
            mainItemClass='form-control border no-focus-outline'
          >
            <div>
              {[
                { value: 'Organisation', label: teamName },
                { value: 'Stakeholder', label: 'Stakeholder' },
              ].map(({ value, label }) => (
                <div key={value} className='radio-button px-3 py-2'>
                  <input
                    id={`initiator-${value}`}
                    type='radio'
                    checked={form.initiator === value}
                    onChange={() => onChange('initiator', value)}
                  />
                  <label htmlFor={`initiator-${value}`}>{label}</label>
                </div>
              ))}
            </div>
          </DropdownComponent>
        </div>
      </div>

      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0 d-flex flex-column' style={{ minWidth: 0 }}>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <label className='paragraph-p2 font-weight-bold mb-0'>People</label>
            {form.members?.length > 0 && (
              <span className='paragraph-p3' style={{ color: 'rgba(0,18,43,0.6)' }}>
                {form.members.length} member{form.members.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <MembersSelect value={form.members ?? []} onChange={(selected) => onChange('members', selected)} placeholder='Add member' />
        </div>
        <div className='col pl-2 pr-0 d-flex flex-column' style={{ minWidth: 0 }}>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <label className='paragraph-p2 font-weight-bold mb-0'>Stakeholders</label>
            {form.stakeholders?.length > 0 && (
              <span className='paragraph-p3' style={{ color: 'rgba(0,18,43,0.6)' }}>
                {form.stakeholders.length} stakeholder{form.stakeholders.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <PeopleAsyncSelect
            value={form.stakeholders ?? []}
            onChange={(selected) => onChange('stakeholders', selected ?? [])}
            placeholder='Add stakeholder'
          />
        </div>
      </div>

      <div className='mb-3'>
        <label className='d-block mb-1 paragraph-p2 font-weight-bold'>Description</label>
        <div className='influence-description interaction-form-description w-100' style={{ display: 'flow-root' }}>
          <DescriptionTinyEditor
            value={form.description}
            onChangeFunction={(content) => onChange('description', content)}
            minHeight={200}
            maxHeight={300}
            newline_behavior='block'
            hideSkeleton
            autoresize_bottom_margin={14}
            disableAutofocus
          />
        </div>
      </div>
    </>
  );
};

export default InteractionFormFields;
